/**
 * STEP 1: CLIENT INTEL
 * 
 * Who are we dealing with? Full picture.
 * 
 * Fields:
 * - Contact info (name, phone, email, preferred contact, best time)
 * - Decision makers (primary, spouse involvement)
 * - Referral info (source, details)
 * - Competition (other quotes)
 * - Property info (type, ownership, age, sqft, HOA)
 * 
 * Theme: Spec Ops
 */

import React from 'react';
import StepContainer from './StepContainer';

const Step1ClientIntel = ({ 
  data = {},
  context = {},
  onUpdate,
  onSave,
  onPrev,
  onNext,
  saving = false,
  isComplete = false,
  validationErrors = [],
}) => {
  const updateField = (key, value) => {
    if (isComplete) return;
    onUpdate?.('client_intel', { ...data, [key]: value });
  };

  return (
    <StepContainer
      step={1}
      onPrev={onPrev}
      onNext={onNext}
      onSave={onSave}
      canGoPrev={false}
      isSaving={saving}
      isComplete={isComplete}
      validationErrors={validationErrors}
    >
      <div className="space-y-6 max-w-4xl">
        {/* Pre-loaded Context from Appointment */}
        {context?.appointment && (
          <div className="p-4 bg-slate-800/50 border border-slate-700 rounded">
            <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">
              Mission Context (from appointment)
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Client:</span>{' '}
                <span className="text-slate-200">{context.appointment.lead_contact_name || 'N/A'}</span>
              </div>
              <div>
                <span className="text-slate-500">Address:</span>{' '}
                <span className="text-slate-200">{context.appointment.location_address || 'N/A'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Contact Information */}
        <div className="space-y-4">
          <h3 className="text-emerald-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            Contact Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Contact Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={data.contact_name || ''}
                onChange={(e) => updateField('contact_name', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                placeholder="Full name"
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Phone <span className="text-red-400">*</span>
              </label>
              <input
                type="tel"
                value={data.contact_phone || ''}
                onChange={(e) => updateField('contact_phone', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                placeholder="(555) 555-5555"
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Email
              </label>
              <input
                type="email"
                value={data.contact_email || ''}
                onChange={(e) => updateField('contact_email', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                placeholder="email@example.com"
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Preferred Contact Method
              </label>
              <select
                value={data.preferred_contact_method || ''}
                onChange={(e) => updateField('preferred_contact_method', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select...</option>
                <option value="phone">Phone</option>
                <option value="text">Text</option>
                <option value="email">Email</option>
              </select>
            </div>
          </div>
        </div>

        {/* Decision Makers */}
        <div className="space-y-4">
          <h3 className="text-emerald-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            Decision Makers
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Who makes the final decision? <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={data.decision_makers?.primary || ''}
                onChange={(e) => updateField('decision_makers', { 
                  ...(data.decision_makers || {}), 
                  primary: e.target.value 
                })}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                placeholder="Name"
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Spouse/Partner involved?
              </label>
              <select
                value={data.spouse_involved || ''}
                onChange={(e) => updateField('spouse_involved', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select...</option>
                <option value="yes_present">Yes - present today</option>
                <option value="yes_not_present">Yes - not present</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>
        </div>

        {/* Referral & Competition */}
        <div className="space-y-4">
          <h3 className="text-emerald-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            Lead Source & Competition
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                How did they find us?
              </label>
              <select
                value={data.referral_source || ''}
                onChange={(e) => updateField('referral_source', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select...</option>
                <option value="google">Google Search</option>
                <option value="referral">Personal Referral</option>
                <option value="nextdoor">Nextdoor</option>
                <option value="facebook">Facebook</option>
                <option value="yard_sign">Yard Sign</option>
                <option value="repeat">Repeat Customer</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Getting other quotes?
              </label>
              <select
                value={data.other_quotes || ''}
                onChange={(e) => updateField('other_quotes', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select...</option>
                <option value="no">No - only us</option>
                <option value="yes_1_2">Yes - 1-2 others</option>
                <option value="yes_3_plus">Yes - 3+ others</option>
                <option value="unsure">Unsure</option>
              </select>
            </div>
          </div>
        </div>

        {/* Property Information */}
        <div className="space-y-4">
          <h3 className="text-emerald-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            Property Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Property Type <span className="text-red-400">*</span>
              </label>
              <select
                value={data.property_type || ''}
                onChange={(e) => updateField('property_type', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select...</option>
                <option value="single_family">Single Family Home</option>
                <option value="townhouse">Townhouse</option>
                <option value="condo">Condo</option>
                <option value="duplex">Duplex/Multi-family</option>
                <option value="commercial">Commercial</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Property Age (approx)
              </label>
              <select
                value={data.property_age || ''}
                onChange={(e) => updateField('property_age', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select...</option>
                <option value="new">New (0-5 years)</option>
                <option value="newer">Newer (5-15 years)</option>
                <option value="established">Established (15-30 years)</option>
                <option value="older">Older (30-50 years)</option>
                <option value="historic">Historic (50+ years)</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                HOA?
              </label>
              <select
                value={data.hoa_present || ''}
                onChange={(e) => updateField('hoa_present', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select...</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
                <option value="unsure">Unsure</option>
              </select>
            </div>
          </div>
          
          {data.hoa_present === 'yes' && (
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                HOA Restrictions/Requirements
              </label>
              <textarea
                value={data.hoa_restrictions || ''}
                onChange={(e) => updateField('hoa_restrictions', e.target.value)}
                disabled={isComplete}
                rows={2}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-none"
                placeholder="Color restrictions, approval process, etc..."
              />
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="space-y-4">
          <h3 className="text-emerald-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            Additional Notes
          </h3>
          <textarea
            value={data.notes || ''}
            onChange={(e) => updateField('notes', e.target.value)}
            disabled={isComplete}
            rows={3}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
              focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-none"
            placeholder="Any other relevant client information..."
          />
        </div>
      </div>
    </StepContainer>
  );
};

export default Step1ClientIntel;
